import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentDojo } from './useCurrentDojo';
import { supabase } from '@/integrations/supabase/client';
import { syncSubscriptionStatus } from '@/utils/syncSubscriptionStatus';
import { useTrialStatus } from './useTrialStatus'; // Assuming this hook exists and provides trial status

export const usePremiumAccess = () => {
  const { user, profile } = useAuth();
  const { dojo, loading: dojoLoading } = useCurrentDojo();
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trialInfo, setTrialInfo] = useState({
    isInTrial: false,
    trialDaysLeft: 0,
    trialExpired: false
  });

  // Check trial status
  const trialStatus = useTrialStatus();

  const checkPremiumAccess = useCallback(async () => {
    if (!user || !profile?.dojo_id) {
      setHasPremiumAccess(false);
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 Checking premium access for user:', user.id, 'in dojo:', profile.dojo_id);

      // Check both dojo plan and active subscription
      const { data: dojoData, error: dojoError } = await supabase
        .from('dojos')
        .select('plan, is_active, name')
        .eq('id', profile.dojo_id)
        .single();

      if (dojoError) {
        console.error('❌ Error fetching dojo:', dojoError);
        setHasPremiumAccess(false);
        setLoading(false);
        return;
      }

      console.log('🏢 Dojo data:', dojoData);

      // Check for active subscription for THIS dojo
      const { data: dojoSubscription, error: dojoSubError } = await supabase
        .from('subscriptions')
        .select('is_active, payment_status, plan_type, current_period_end')
        .eq('dojo_id', profile.dojo_id)
        .eq('is_active', true)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dojoSubError && dojoSubError.code !== 'PGRST116') {
        console.error('❌ Error fetching dojo subscription:', dojoSubError);
      }

      // Check if this USER has ANY active subscription (cross-dojo access)
      const { data: userSubscription, error: userSubError } = await supabase
        .from('subscriptions')
        .select('is_active, payment_status, plan_type, current_period_end, dojo_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (userSubError && userSubError.code !== 'PGRST116') {
        console.error('❌ Error fetching user subscription:', userSubError);
      }

      // ALSO check user's payment_status for cross-dojo access
      const userHasPaidStatus = profile.payment_status === 'paid';

      console.log('💳 Dojo subscription data:', dojoSubscription);
      console.log('👤 User subscription data:', userSubscription);
      console.log('💰 User payment status:', profile.payment_status);

      // Premium access logic:
      // 1. Dojo has paid/pro plan and is active, OR
      // 2. There's an active paid subscription for this dojo, OR
      // 3. User has ANY active paid subscription (cross-dojo access), OR
      // 4. User has paid status (set by webhook for cross-dojo access)
      let hasDojoAccess = (dojoData.plan === 'pro' || dojoData.plan === 'paid') && dojoData.is_active;

      let hasDojoSubscription = dojoSubscription && dojoSubscription.is_active && dojoSubscription.payment_status === 'paid';
      let hasUserSubscription = userSubscription && userSubscription.is_active && userSubscription.payment_status === 'paid';

      // Check if subscriptions are not expired
      let dojoSubscriptionValid = true;
      if (dojoSubscription?.current_period_end) {
        const endDate = new Date(dojoSubscription.current_period_end);
        dojoSubscriptionValid = endDate > new Date();
      }

      let userSubscriptionValid = true;
      if (userSubscription?.current_period_end) {
        const endDate = new Date(userSubscription.current_period_end);
        userSubscriptionValid = endDate > new Date();
      }

      // Cross-dojo access: User with paid status can access any dojo
      let hasCrossDOjoAccess = userHasPaidStatus || (hasUserSubscription && userSubscriptionValid);

      let hasAccess = hasDojoAccess ||
                       (hasDojoSubscription && dojoSubscriptionValid) ||
                       hasCrossDOjoAccess;

      console.log('✅ Premium access result:', {
        hasDojoAccess,
        hasDojoSubscription,
        dojoSubscriptionValid,
        hasUserSubscription,
        userSubscriptionValid,
        hasCrossDOjoAccess,
        userHasPaidStatus,
        finalAccess: hasAccess
      });

      // If no premium access found but user/dojo should have it, try auto-sync
      if (!hasAccess && profile?.dojo_id) {
        console.log('🔄 No premium access found, attempting auto-sync...');

        try {
          // Try to sync by user email first (cross-dojo access)
          await syncSubscriptionStatus(undefined, profile.email);
          console.log('✅ Auto-sync completed, re-checking access...');

          // Re-check after sync
          const { data: syncedDojoSubscription } = await supabase
            .from('subscriptions')
            .select('is_active, payment_status, plan_type, current_period_end')
            .eq('dojo_id', profile.dojo_id)
            .eq('is_active', true)
            .eq('payment_status', 'paid')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          const { data: syncedUserSubscription } = await supabase
            .from('subscriptions')
            .select('is_active, payment_status, plan_type, current_period_end, dojo_id')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .eq('payment_status', 'paid')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (syncedDojoSubscription) {
            console.log('🎉 Found synced dojo subscription:', syncedDojoSubscription);
            hasDojoAccess = true;
            hasDojoSubscription = syncedDojoSubscription;
            dojoSubscriptionValid = new Date(syncedDojoSubscription.current_period_end) > new Date();
          }

          if (syncedUserSubscription) {
            console.log('🎉 Found synced user subscription:', syncedUserSubscription);
            hasUserSubscription = syncedUserSubscription;
            userSubscriptionValid = new Date(syncedUserSubscription.current_period_end) > new Date();
            hasCrossDOjoAccess = userSubscriptionValid;
          }
        } catch (syncError) {
          console.error('❌ Auto-sync failed:', syncError);

          // If subscription table doesn't exist, create a dummy subscription based on Stripe data
          try {
            console.log('🔄 Attempting to create subscription from user payment status...');

            // Only create subscription if user has payment status but no subscription record
            if (!hasDojoSubscription && !hasUserSubscription) {
              try {
                console.log('🔄 Creating subscription record from user payment status...');

                if (profile.payment_status === 'paid') {
                  const { error: createSubError } = await supabase
                    .from('subscriptions')
                    .insert({
                      user_id: user.id,
                      dojo_id: profile.dojo_id,
                      plan_type: 'monthly',
                      is_active: true,
                      payment_status: 'paid',
                      current_period_start: new Date().toISOString(),
                      current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
                      created_at: new Date().toISOString()
                    });

                  if (!createSubError) {
                    console.log('✅ Created subscription record from payment status');
                    hasDojoAccess = true;
                    hasDojoSubscription = {
                      is_active: true,
                      payment_status: 'paid',
                      plan_type: 'monthly',
                      current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                    };
                    dojoSubscriptionValid = true;
                  }
                }
              } catch (createError) {
                console.error('❌ Failed to create subscription from payment status:', createError);
              }
            }
          } catch (createError) {
            console.error('❌ Failed to create subscription from payment status:', createError);
          }
        }
      }

      // Final access determination (only based on admin Stripe payments)
      const finalAccess = hasDojoAccess ||
                         (hasDojoSubscription && dojoSubscriptionValid) ||
                         hasCrossDOjoAccess;
    } catch (error) {
      console.error('❌ Error in premium access check:', error);
      setHasPremiumAccess(false);
    } finally {
      setLoading(false);
    }
  }, [user, profile?.dojo_id, profile, profile?.email]); // Added profile and profile.email to dependency array

  useEffect(() => {
    const checkStatus = async () => {
      try {
        if (!profile?.dojo_id) return;

        setHasPremiumAccess(trialStatus.hasActiveAccess);
        setTrialInfo({ isInTrial: trialStatus.isOnTrial, trialDaysLeft: trialStatus.trialDaysLeft, trialExpired: trialStatus.isTrialExpired });
        setLoading(false);
      } catch (error) {
        console.error('❌ Premium access check failed:', error);
        setLoading(false);
      }
    };

    checkStatus();
  }, [profile?.dojo_id, trialStatus.hasActiveAccess, trialStatus.isTrialExpired, trialStatus.isOnTrial, trialStatus.trialDaysLeft]);


  // This useEffect is intended to run after the initial checks to finalize the premium access state
  useEffect(() => {
    console.log('usePremiumAccess effect triggered:', {
      hasProfile: !!profile,
      dojoLoading,
      dojo: dojo ? { name: dojo.name, plan: dojo.plan, is_active: dojo.is_active, trial_start: dojo.trial_start } : null
    });

    if (!profile) {
      console.log('No profile, keeping loading true');
      setLoading(true);
      return;
    }

    // If still loading dojo data, wait for it to complete
    if (dojoLoading && !dojo) {
      console.log('Dojo still loading, waiting...');
      setLoading(true);
      return;
    }

    // CRITICAL: Special exception for Taiga Dojo - always has full access
    const isTaigaDojo = dojo?.name === 'Taiga Dojo' ||
                        dojo?.join_code === 'TAIGA DOJO' ||
                        dojo?.name?.toLowerCase().includes('taiga') ||
                        dojo?.id === '73b9c11e-c3d0-4c06-9de4-f990a63c3d2e';

    console.log('Taiga Dojo check:', {
      isTaigaDojo,
      dojoName: dojo?.name,
      joinCode: dojo?.join_code
    });

    if (isTaigaDojo) {
      console.log('TAIGA DOJO DETECTED - GRANTING FULL ACCESS');
      setHasPremiumAccess(true);
      setTrialInfo({ isInTrial: false, trialDaysLeft: 0, trialExpired: false });
      setLoading(false);
      return;
    }

    // Calculate trial status for regular dojos
    let isInTrial = false;
    let trialDaysLeft = 0;
    let trialExpired = false;

    if (dojo?.plan === 'trial' && dojo?.trial_start) {
      const trialStart = new Date(dojo.trial_start);
      const now = new Date();
      const daysSinceStart = Math.floor((now.getTime() - trialStart.getTime()) / (1000 * 60 * 60 * 24));
      trialDaysLeft = Math.max(0, 14 - daysSinceStart);
      isInTrial = true;
      trialExpired = trialDaysLeft <= 0;
    }

    // NEW PREMIUM ACCESS LOGIC:
    // 1. Paid plan with active status = full access for all users in dojo
    // 2. Trial plan within 14 days = full access
    // 3. Trial plan after 14 days = basic only
    // 4. Pro plan (legacy) = full access
    // 5. Admins get access when their dojo has paid plan

    const hasDojoAccess = (dojo?.plan === 'paid' && dojo?.is_active === true) ||
                         (dojo?.plan === 'pro' && dojo?.is_active === true);

    const hasUserAccess = (profile.payment_status === 'paid'); // Direct user payment status

    const hasCrossDOjoAccess = (dojo?.plan === 'paid' || dojo?.plan === 'pro') &&
                              (profile.payment_status === 'paid');

    const isOnTrial = dojo?.plan === 'trial';
    const isTrialExpired = isOnTrial && trialDaysLeft <= 0;


    console.log('🔐 Premium Access Logic:', {
      hasDojoAccess,
      hasUserAccess,
      hasCrossDOjoAccess,
      isOnTrial,
      trialDaysLeft,
      isTrialExpired,
      isTaigaDojo
    });

    // Final access determination - include trial access
    const finalAccess = hasDojoAccess || hasUserAccess || hasCrossDOjoAccess || (isOnTrial && !isTrialExpired);

    setHasPremiumAccess(finalAccess);
    setTrialInfo({ isInTrial: isOnTrial && !isTrialExpired, trialDaysLeft, trialExpired });
    setLoading(false);

    // Ensure checkPremiumAccess is called to fetch subscription data
    // This might lead to a double call if not managed carefully.
    // Relying on the initial checkPremiumAccess call is better.
    // checkPremiumAccess(); // Avoid calling again if already handled

  }, [profile, dojo, dojoLoading, checkPremiumAccess, trialStatus.hasActiveAccess, trialStatus.isTrialExpired, trialStatus.isOnTrial, trialStatus.trialDaysLeft]);


  return {
    hasPremiumAccess,
    loading,
    isPaidDojo: (dojo?.plan === 'paid' || dojo?.plan === 'pro') && dojo?.is_active === true,
    isTaigaDojo: dojo?.name === 'Taiga Dojo' || dojo?.join_code === 'TAIGA DOJO',
    ...trialInfo,
    // Helper methods for UI
    shouldShowUpgradePrompt: trialInfo.trialExpired && dojo?.plan === 'trial',
    planStatus: dojo?.plan === 'paid' || dojo?.plan === 'pro' ? 'paid' :
                dojo?.plan === 'trial' && !trialInfo.trialExpired ? 'trial_active' :
                dojo?.plan === 'trial' && trialInfo.trialExpired ? 'trial_expired' : 'unknown'
  };
};